import { useEffect, useState } from "react";
import { AlertTriangle, CalendarCheck, FileText, Lock, QrCode, Receipt } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, brl, dataBR, openPdf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, Surface } from "@/components/ds";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";

const STATUS = {
  AGUARDANDO_PAGAMENTO: { label: "Aguardando pagamento", cls: "border-amber-500/40 bg-amber-500/15 text-amber-300" },
  PAGAMENTO_INFORMADO: { label: "Pagamento informado", cls: "border-brand/40 bg-brand/15 text-brand" },
  PAGO: { label: "Pago", cls: "border-emerald-500/40 bg-emerald-500/15 text-emerald-300" },
};

const Row = ({ label, value, testid }) => (
  <div className="flex items-center justify-between gap-3 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-num" data-testid={testid}>
      {value}
    </span>
  </div>
);

export function PartnerClosures({ preview, closures, onReload }) {
  const pdf = async (path) => {
    try {
      await openPdf(path);
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const [busy, setBusy] = useState(false);
  const [pixFor, setPixFor] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [confirmEarly, setConfirmEarly] = useState(false);

  const generate = async (early = false) => {
    setBusy(true);
    try {
      const { data } = await api.post("/partner/closures", {
        period: preview.period,
        early,
      });
      toast.success(`Fechamento ${data.number} gerado.`);
      setConfirmEarly(false);
      onReload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const lock = preview.replenish_lock || {};

  return (
    <div className="space-y-6" data-testid="partner-closures">
      <Surface className="space-y-3 p-5" data-testid="closure-preview">
        <div className="flex items-center justify-between gap-3">
          <p className="font-display text-base">Período atual · {preview.period_label}</p>
          <span className="text-[11px] text-muted-foreground">
            fecha em {dataBR(preview.period_end)}
          </span>
        </div>
        <Row label="Estoque inicial" value={`${preview.stock_initial} un`} testid="preview-initial" />
        <Row label="Reposições" value={`${preview.replenished} un`} testid="preview-replenished" />
        <Row label="Vendas" value={`${preview.sold} un`} testid="preview-sold" />
        <Row label="Devoluções" value={`${preview.returned} un`} testid="preview-returned" />
        <Row label="Estoque final" value={`${preview.stock_final} un`} testid="preview-final" />
        {Number(preview.adjusted || 0) !== 0 && (
          <Row label="Ajustes" value={`${preview.adjusted} un`} testid="preview-adjusted" />
        )}
        {(preview.lines || []).length > 0 && (
          <ul className="space-y-1 rounded-lg border border-white/10 bg-secondary/40 p-3" data-testid="preview-lines">
            {preview.lines.map((l) => (
              <li key={l.key} className="flex items-center justify-between gap-2 text-xs" data-testid={`preview-line-${l.key}`}>
                <span className="min-w-0 truncate text-muted-foreground">{l.name}</span>
                <span className="shrink-0 font-num">
                  ini {l.stock_initial} · rep {l.replenished} · vend {l.sold} · dev {l.returned} ·
                  fim {l.stock_final}
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className="flex items-center justify-between gap-3 border-t border-white/10 pt-3">
          <span className="text-sm text-muted-foreground">Valor devido</span>
          <span className="font-num text-xl text-brand" data-testid="preview-amount">
            {brl(preview.amount_due)}
          </span>
        </div>
        {preview.can_close ? (
          <Button
            onClick={() => generate(false)}
            disabled={busy}
            data-testid="generate-closure-btn"
            className="h-11 w-full rounded-full font-semibold"
          >
            {busy ? "Gerando..." : "Gerar fechamento do período"}
          </Button>
        ) : preview.can_close_early ? (
          <div className="space-y-2">
            <Button
              variant="outline"
              onClick={() => setConfirmEarly(true)}
              disabled={busy}
              data-testid="close-early-btn"
              className="h-11 w-full gap-2 rounded-full font-semibold"
            >
              <CalendarCheck className="h-4 w-4" /> Fechar período antecipadamente
            </Button>
            <p className="text-[11px] text-muted-foreground" data-testid="closure-preview-hint">
              O fechamento oficial deste período é em {dataBR(preview.period_end)}. Você pode
              encerrar antes, se preferir.
            </p>
          </div>
        ) : (
          <p className="text-[11px] text-muted-foreground" data-testid="closure-preview-hint">
            {preview.already_closed
              ? "O fechamento deste período já foi gerado."
              : `Você poderá gerar o fechamento a partir de ${dataBR(preview.period_end)}.`}
          </p>
        )}
      </Surface>

      {(lock.blocked || lock.pending) && (
        <Surface
          className={`flex flex-col gap-1 p-4 ${
            lock.blocked
              ? "border-red-500/40 bg-red-500/10"
              : "border-amber-500/40 bg-amber-500/10"
          }`}
          data-testid="closure-lock-alert"
        >
          <p
            className={`flex items-center gap-2 text-sm font-semibold ${
              lock.blocked ? "text-red-300" : "text-amber-300"
            }`}
          >
            {lock.blocked ? <Lock className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
            {lock.blocked ? "Reposições indisponíveis" : "Fechamento pendente"}
          </p>
          <p className="text-xs text-muted-foreground">
            {lock.blocked
              ? "Novas reposições estão temporariamente indisponíveis. Entre em contato com a TR3ND para mais informações."
              : lock.overdue
              ? `Seu fechamento de ${lock.period_label} está em atraso desde ${dataBR(lock.regularize_until)}. Faça o fechamento para regularizar.`
              : `Seu fechamento de ${lock.period_label} ainda não foi realizado. Regularize até ${dataBR(lock.regularize_until)}.`}
          </p>
        </Surface>
      )}

      {confirmEarly && (
        <ResponsiveModal
          open
          onOpenChange={(o) => !o && setConfirmEarly(false)}
          title="Fechar período antecipadamente?"
          description="Este fechamento será calculado com todas as vendas, devoluções e movimentações registradas até este momento. Após o fechamento, novas movimentações de vendas deverão pertencer ao próximo período."
          testid="close-early-modal"
        >
          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            <Button
              variant="outline"
              onClick={() => setConfirmEarly(false)}
              className="h-11 flex-1 rounded-full"
              data-testid="close-early-cancel"
            >
              Cancelar
            </Button>
            <Button
              onClick={() => generate(true)}
              disabled={busy}
              className="h-11 flex-1 rounded-full font-semibold"
              data-testid="close-early-confirm"
            >
              {busy ? "Fechando..." : "Fechar período"}
            </Button>
          </div>
        </ResponsiveModal>
      )}

      <div>
        <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Meus fechamentos
        </p>
        {closures.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhum fechamento gerado ainda.</p>
        ) : (
          <div className="space-y-3">
            {closures.map((c) => (
              <Surface key={c.id} className="min-w-0 overflow-hidden p-5" data-testid={`closure-card-${c.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-num text-sm">{c.number}</p>
                    <p className="text-xs text-muted-foreground">{c.period_label}</p>
                  </div>
                  <span
                    className={`shrink-0 rounded-full border px-2.5 py-1 text-xs ${STATUS[c.status]?.cls}`}
                    data-testid={`closure-status-${c.id}`}
                  >
                    {STATUS[c.status]?.label || c.status}
                  </span>
                </div>
                <div className="mt-3 space-y-1.5">
                  <Row label="Vendas no período" value={`${c.sold} un`} />
                  <Row label="Estoque final" value={`${c.stock_final} un`} />
                  <Row label="Valor vendido" value={brl(c.sales_value)} />
                </div>
                {(c.lines || []).length > 0 && (
                  <ul className="mt-3 space-y-1 rounded-lg border border-white/10 bg-secondary/40 p-3" data-testid={`closure-lines-${c.id}`}>
                    {c.lines.map((l) => (
                      <li key={l.key} className="flex items-center justify-between gap-2 text-xs">
                        <span className="min-w-0 truncate text-muted-foreground">{l.name}</span>
                        <span className="shrink-0 font-num">
                          vend {l.sold} · fim {l.stock_final}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-3 flex items-center justify-between gap-3 border-t border-white/10 pt-3">
                  <span className="text-sm text-muted-foreground">Valor devido</span>
                  <span className="font-num text-lg text-brand" data-testid={`closure-amount-${c.id}`}>
                    {brl(c.amount_due)}
                  </span>
                </div>
                {c.payment && (
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    Pagamento informado em {dataBR(c.payment.informed_at)}
                    {c.paid_confirmed_at ? ` · confirmado em ${dataBR(c.paid_confirmed_at)}` : ""}
                  </p>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => pdf(`/partner/closures/${c.id}/pdf`)}
                  data-testid={`closure-pdf-btn-${c.id}`}
                  className="mt-3 h-9 gap-1.5 rounded-full text-xs"
                >
                  <FileText className="h-3.5 w-3.5" /> Gerar PDF
                </Button>
                {c.status !== "PAGO" && Number(c.amount_due) > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      onClick={() => setPixFor(c)}
                      data-testid={`closure-pix-btn-${c.id}`}
                      className="h-9 gap-1.5 rounded-full text-xs font-semibold"
                    >
                      <QrCode className="h-3.5 w-3.5" /> Pagar com PIX
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPayFor(c)}
                      data-testid={`closure-inform-btn-${c.id}`}
                      className="h-9 gap-1.5 rounded-full text-xs"
                    >
                      <Receipt className="h-3.5 w-3.5" /> Informar pagamento
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        try {
                          const { data } = await api.get(`/partner/closures/${c.id}/whatsapp`);
                          window.open(data.url, "_blank");
                        } catch (e) {
                          toast.error(apiError(e));
                        }
                      }}
                      data-testid={`closure-whatsapp-btn-${c.id}`}
                      className="h-9 rounded-full text-xs"
                    >
                      Avisar no WhatsApp
                    </Button>
                  </div>
                )}
                {Number(c.amount_due) === 0 && c.status !== "PAGO" && (
                  <p className="mt-3 text-[11px] text-muted-foreground" data-testid={`closure-nothing-due-${c.id}`}>
                    Nada a pagar neste período.
                  </p>
                )}
              </Surface>
            ))}
          </div>
        )}
      </div>

      {pixFor && <PixModal closure={pixFor} onClose={() => setPixFor(null)} />}
      {payFor && (
        <PaymentModal closure={payFor} onClose={() => setPayFor(null)} onSaved={onReload} />
      )}
    </div>
  );
}

function PixModal({ closure, onClose }) {
  const [pix, setPix] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .post(`/partner/closures/${closure.id}/pix-charge`)
      .then(({ data }) => setPix(data))
      .catch((e) => setError(apiError(e)));
  }, [closure.id]);

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Pagamento via PIX"
      description={`${closure.number} · ${closure.period_label}`}
      testid="closure-pix-modal"
    >
      <div className="mt-2 space-y-4 text-center">
        <p className="font-num text-2xl text-brand" data-testid="pix-amount">
          {brl(closure.amount_due)}
        </p>
        {error && <p className="text-sm text-red-300" data-testid="pix-error">{error}</p>}
        {pix && (
          <>
            <img
              src={pix.qr_code}
              alt="QR Code PIX"
              className="mx-auto h-56 w-56 rounded-lg bg-white p-2"
              data-testid="pix-qr-image"
            />
            <div className="text-left">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">PIX copia e cola</p>
              <p className="mt-1 max-h-24 overflow-y-auto break-all rounded-lg border border-white/10 bg-secondary/40 p-3 text-[11px]" data-testid="pix-brcode">
                {pix.brcode}
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  navigator.clipboard?.writeText(pix.brcode);
                  toast.success("Código PIX copiado.");
                }}
                data-testid="copy-pix-btn"
                className="mt-3 h-10 w-full rounded-full text-xs"
              >
                Copiar código PIX
              </Button>
            </div>
          </>
        )}
      </div>
    </ResponsiveModal>
  );
}

function PaymentModal({ closure, onClose, onSaved }) {
  const [form, setForm] = useState({ paid_at: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/partner/closures/${closure.id}/payment`, {
        paid_at: form.paid_at || null,
        notes: form.notes,
      });
      toast.success("Pagamento informado! A TR3ND vai confirmar.");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Informar pagamento"
      description={`${closure.number} · ${brl(closure.amount_due)}`}
      testid="closure-payment-modal"
    >
      <form onSubmit={submit} className="mt-2 flex flex-col gap-4">
        <Field label="Data do pagamento">
          <Input
            type="date"
            value={form.paid_at}
            onChange={(e) => setForm((f) => ({ ...f, paid_at: e.target.value }))}
            className="font-num"
            data-testid="payment-date-input"
          />
        </Field>
        <Field label="Observação">
          <Textarea
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            rows={2}
            placeholder="Ex.: paguei pelo PIX às 14h"
            data-testid="payment-notes-input"
          />
        </Field>
        <div className="flex justify-end gap-2 border-t border-white/10 pt-4">
          <Button type="button" variant="outline" onClick={onClose} className="rounded-full" data-testid="payment-cancel-btn">
            Cancelar
          </Button>
          <Button type="submit" disabled={busy} className="rounded-full" data-testid="payment-submit-btn">
            {busy ? "Enviando..." : "Informar pagamento"}
          </Button>
        </div>
      </form>
    </ResponsiveModal>
  );
}
